
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ApplicationSectionProgress } from './ApplicationSectionProgress';
import { useApplicationProgress } from '@/hooks/use-application-progress';
import { SECTIONS } from '@/constants/application';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { usePEOProvider } from '@/hooks/use-peo-provider';
import type { Application } from '@/types/database.types';

interface ReviewAndSubmitProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
  onSectionChange: (section: number) => void;
}

export const ReviewAndSubmit = ({ data, onSave, onSectionChange }: ReviewAndSubmitProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCertified, setIsCertified] = useState(false);
  const [showCertificationError, setShowCertificationError] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getCompletedSections } = useApplicationProgress(data as Application);
  const { peoProvider, isPEOUser } = usePEOProvider();
  
  const completedSections = getCompletedSections();
  const allSectionsComplete = Array.from({ length: SECTIONS.length - 1 }, (_, i) => i)
    .every(i => completedSections.includes(i));

  // Debug logging to understand the completion state
  console.log('ReviewAndSubmit Debug:', {
    completedSections,
    totalSections: SECTIONS.length - 1,
    allSectionsComplete,
    isCertified,
    buttonDisabled: isSubmitting || !isCertified,
    applicationId: data.id
  });

  const handleSubmit = async () => {
    console.log('=== SUBMISSION PROCESS STARTED ===');
    
    // Check certification first - this is the primary requirement
    if (!isCertified) {
      console.log('Submission blocked: Certification not acknowledged');
      setShowCertificationError(true);
      toast({
        title: "Certification required",
        description: "Please acknowledge the certification statement to submit your application.",
        variant: "destructive",
      });
      return;
    }

    // Warn about incomplete sections but don't block submission if certified
    if (!allSectionsComplete) {
      console.log('Warning: Not all sections complete, but certification acknowledged');
      toast({
        title: "Application submitted with incomplete sections",
        description: "Your application has been submitted. You may want to complete remaining sections for better PEO matching.",
      });
    }

    setIsSubmitting(true);
    try {
      console.log('Step 1: Verifying user authentication...');
      
      // First, verify the user is still authenticated
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Authentication session error:', sessionError);
        throw new Error('Authentication session error: ' + sessionError.message);
      }
      
      if (!session) {
        console.error('No active session found');
        toast({
          title: "Authentication required",
          description: "Your session has expired. Please log in again to submit your application.",
          variant: "destructive",
        });
        navigate('/login');
        return;
      }

      console.log('Step 2: User authenticated successfully, user ID:', session.user.id);

      // Attempt to save the application with submitted status
      try {
        console.log('Step 3: Saving application with submitted status...');
        await onSave({ status: 'submitted' });
        console.log('Step 4: Application save completed successfully');
      } catch (saveError) {
        console.error('Save operation failed:', saveError);
        
        // Check if it's an authentication error
        if (saveError instanceof Error && saveError.message.includes('auth')) {
          console.error('Authentication error during save');
          toast({
            title: "Authentication error",
            description: "Your session expired during submission. Please log in again.",
            variant: "destructive",
          });
          navigate('/login');
          return;
        }
        
        // Generic save error
        throw new Error('Failed to save application: ' + (saveError instanceof Error ? saveError.message : 'Unknown error'));
      }
      
      // Auto-generate quote for PEO users
      const createdByPeoId = (data as any)?.created_by_peo_id;
      if (isPEOUser && peoProvider && data.id && createdByPeoId) {
        console.log('Step 5: Auto-generating quote for PEO user...');
        try {
          const { error: quoteError } = await supabase.rpc('create_peo_quote_for_application', {
            p_application_id: data.id,
            p_provider_id: createdByPeoId,
            p_created_by: session.user.id
          });

          if (quoteError) {
            console.error('Error creating quote:', quoteError);
            // Don't fail the submission, just log the error
            toast({
              title: "Application submitted with warning",
              description: "Your application was submitted but there was an issue creating the quote. You can create it manually from your dashboard.",
              variant: "default",
            });
          } else {
            console.log('Quote created successfully for PEO user');
          }
        } catch (quoteError) {
          console.error('Exception creating quote:', quoteError);
          // Don't fail the submission
        }
      }

      // Only set session storage AFTER successful save
      if (data.id) {
        console.log('Step 6: Setting session storage with application ID:', data.id);
        sessionStorage.setItem('submittedApplicationId', data.id);
        sessionStorage.setItem('applicationSubmitted', 'true');
        console.log('Session storage set successfully:', {
          submittedApplicationId: data.id,
          applicationSubmitted: 'true'
        });
      } else {
        console.error('No application ID available after save');
        throw new Error('Application ID not available after save');
      }
      
      console.log('Step 7: Showing success toast');
      const isFromPEO = isPEOUser && (data as any)?.created_by_peo_id;
      toast({
        title: "Application submitted successfully",
        description: isFromPEO 
          ? "Application has been submitted and is ready for quote creation in your dashboard."
          : "Your application has been submitted to our PEO partners.",
      });
      
      // Navigate based on user type
      console.log('Step 8: Navigating based on user type...');
      try {
        if (isFromPEO) {
          console.log('PEO user detected, navigating to PEO dashboard');
          navigate('/peo/dashboard', { replace: true });
        } else {
          console.log('Regular user, navigating to success page');
          navigate('/success', { replace: true });
        }
        console.log('Navigation initiated successfully');
      } catch (navError) {
        console.error('Navigation error:', navError);
        // Fallback to window navigation if React Router fails
        console.log('Falling back to window.location.href...');
        if (isFromPEO) {
          window.location.href = '/peo/dashboard';
        } else {
          window.location.href = '/success';
        }
      }
      
    } catch (error) {
      console.error('=== SUBMISSION ERROR ===', error);
      
      // Clear any partial session storage on error
      sessionStorage.removeItem('submittedApplicationId');
      sessionStorage.removeItem('applicationSubmitted');
      
      let errorMessage = "There was an error submitting your application. Please try again.";
      
      if (error instanceof Error) {
        if (error.message.includes('auth') || error.message.includes('session')) {
          errorMessage = "Your session has expired. Please refresh the page and try again.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Submission failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      console.log('=== SUBMISSION PROCESS COMPLETED ===');
    }
  };

  const handleCertificationChange = (checked: boolean) => {
    setIsCertified(checked);
    if (checked) {
      setShowCertificationError(false);
    }
    console.log('Certification changed:', checked);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">Review & Submit</h2>
          </div>
          
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
            <p className="text-muted-foreground text-sm">
              Final review ensures accuracy and completeness before submission—maximizing the quality of PEO matches and accuracy of the quotes.
            </p>
          </div>

          <ApplicationSectionProgress 
            data={data} 
            onSectionChange={onSectionChange}
          />

          {/* Certification Section */}
          <Card className={`transition-all duration-300 ${
            showCertificationError 
              ? 'border-destructive bg-destructive/5 shadow-lg ring-2 ring-destructive/20' 
              : 'border-border'
          }`}>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  {showCertificationError && (
                    <AlertTriangle className="w-5 h-5 text-destructive mt-1 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className={`font-medium mb-2 text-center ${
                      showCertificationError ? 'text-destructive' : 'text-foreground'
                    }`}>
                      Required Certification
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      I certify that to the best of my knowledge and ability this information is complete and accurate, 
                      and as a representative of the organization, I authorize The PEO App, Inc. to shop, obtain quotes, 
                      and negotiate on this organization's behalf.
                    </p>
                  </div>
                </div>
                
                <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
                  showCertificationError 
                    ? 'border-destructive bg-destructive/5' 
                    : 'border-border bg-background'
                }`}>
                  <Switch
                    id="certification"
                    checked={isCertified}
                    onCheckedChange={handleCertificationChange}
                    className="data-[state=checked]:bg-primary"
                  />
                  <label 
                    htmlFor="certification" 
                    className={`text-sm font-medium cursor-pointer ${
                      showCertificationError ? 'text-destructive' : 'text-foreground'
                    }`}
                  >
                    {isCertified ? 'Certified ✓' : 'Click to certify'}
                  </label>
                </div>

                {showCertificationError && (
                  <p className="text-sm text-destructive font-medium">
                    You must acknowledge this certification to submit your application.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-center pt-6">
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting || !isCertified}
              size="lg"
              className="px-8"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>

          {!isCertified && (
            <div className="text-center text-sm text-muted-foreground">
              <p className={showCertificationError ? 'text-destructive font-medium' : ''}>
                Acknowledge the certification statement to enable submission
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
